/**
 * Sprint 24 — EquippedOnHires.
 *
 * Detail-page card listing the agents (hires) equipped with a connection.
 * Shared by the MCP and Wrapped detail pages. Reads list_agents_equipped_with.
 */

import type { CSSProperties } from "react";
import { useNavigate } from "react-router";
import {
  type ConnectionKind,
  useAgentsEquippedWith,
} from "../../../hooks/use-connections";
import { NodeAvatar } from "../primitives";

export interface EquippedOnHiresProps {
  connectionKind: ConnectionKind;
  connectionId: string;
}

export function EquippedOnHires({ connectionKind, connectionId }: EquippedOnHiresProps) {
  const navigate = useNavigate();
  const { data: agents, isLoading } = useAgentsEquippedWith(connectionKind, connectionId);

  return (
    <section style={card}>
      <div style={heading}>Equipped on {(agents ?? []).length} hires</div>
      {isLoading ? (
        <div style={muted}>Loading…</div>
      ) : (agents ?? []).length === 0 ? (
        <div style={empty}>
          No hires equipped with this connection yet.{" "}
          <button type="button" style={link} onClick={() => navigate("/workforce")}>
            Equip a hire →
          </button>
        </div>
      ) : (
        (agents ?? []).map((a) => (
          <button
            key={a.node_id}
            type="button"
            style={hireRow}
            onClick={() => navigate(`/workforce/${a.node_id}`)}
          >
            <NodeAvatar type="agent" name={a.name} size="sm" />
            <span style={hireName}>{a.name}</span>
            {a.role && <span style={hireRole}>{a.role}</span>}
          </button>
        ))
      )}
    </section>
  );
}

const card: CSSProperties = {
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-md)",
  border: "0.5px solid var(--color-border-secondary)",
  padding: "10px 14px",
  display: "flex",
  flexDirection: "column",
  gap: 6,
  fontFamily: "var(--font-sans)",
};

const heading: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--color-text-tertiary)",
  marginBottom: 2,
};

const hireRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  background: "transparent",
  border: "none",
  padding: "2px 0",
  cursor: "pointer",
  textAlign: "left",
  width: "100%",
};

const hireName: CSSProperties = {
  fontSize: 12,
  flex: 1,
  color: "var(--color-text-primary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const hireRole: CSSProperties = {
  fontSize: 10,
  color: "var(--color-text-tertiary)",
};

const muted: CSSProperties = { fontSize: 12, color: "var(--color-text-tertiary)" };

const empty: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-tertiary)",
  lineHeight: 1.5,
};

const link: CSSProperties = {
  background: "transparent",
  border: "none",
  padding: 0,
  cursor: "pointer",
  color: "var(--color-text-info)",
  fontSize: 12,
  fontFamily: "var(--font-sans)",
};
